import React, { useState } from 'react';
import api from '../utils/api';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalInput, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { TerminalSelect, TerminalLabel, TerminalAlert } from './shared/TerminalForms';


const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
`;

const Title = styled(TerminalTitle)`
  margin-bottom: 2rem;
  font-size: 2rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const Description = styled(TerminalText)`
  font-size: 1rem;
  color: ${theme.colors.textSecondary};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const HelperText = styled.small`
  display: block;
  margin-top: 0.5rem;
  color: ${theme.colors.textMuted};
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
`;

const ResultCard = styled(TerminalCard)`
  margin-top: 2rem;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const ValidationStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatusText = styled.h2`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${props => props.isValid ? theme.colors.success : theme.colors.error};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const NetworkChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.borderBright};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fonts.mono};
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  color: ${theme.colors.primary};
`;

const ResultSection = styled.div`
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid ${theme.colors.border};
`;

const SectionTitle = styled.h3`
  font-family: ${theme.fonts.mono};
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '[ ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' ]';
    color: ${theme.colors.primary};
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.strong`
  min-width: 120px;
  font-weight: 600;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  text-transform: uppercase;
`;

const AddressDisplay = styled.code`
  font-family: ${theme.fonts.mono};
  background: ${theme.colors.bgTertiary};
  padding: 0.5rem 0.75rem;
  border-radius: ${theme.borderRadius.sm};
  word-break: break-all;
  font-size: 0.875rem;
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.border};
`;

const Chip = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  font-family: ${theme.fonts.mono};
  border: 1px solid;
  
  &.success {
    background: rgba(0, 255, 65, 0.1);
    color: ${theme.colors.success};
    border-color: ${theme.colors.success};
  }
  
  &.error {
    background: rgba(255, 0, 64, 0.1);
    color: ${theme.colors.error};
    border-color: ${theme.colors.error};
  }
  
  &.warning {
    background: rgba(255, 170, 0, 0.1);
    color: ${theme.colors.warning};
    border-color: ${theme.colors.warning};
  }
  
  &.info {
    background: rgba(0, 255, 255, 0.1);
    color: ${theme.colors.accent};
    border-color: ${theme.colors.accent};
  }
  
  &.primary {
    background: rgba(0, 255, 65, 0.1);
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
  }
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Metadata = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  text-align: center;
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
`;

const AddressValidator = () => {
  const [address, setAddress] = useState('');
  const [network, setNetwork] = useState('mainnet');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleValidate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.get('/validate-bitcoin-address',
        {
          params: {
            address: address,
            network: network
          }
        }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred while validating the address');
    } finally {
      setLoading(false);
    }
  };

  const getNetworkLabel = (networkType) => {
    switch (networkType) {
      case 'mainnet': return 'MAINNET';
      case 'testnet': return 'TESTNET';
      case 'both': return 'BOTH';
      default: return networkType.toUpperCase();
    }
  };

  return (
    <Container>
      <Title>Bitcoin Address Validator</Title>

      <TerminalCard>
        <Description>
          Validate Bitcoin addresses against mainnet, testnet, or both networks
        </Description>
        
        <InputGroup>
          <TerminalLabel htmlFor="address">Bitcoin Address</TerminalLabel>
          <TerminalInput
            id="address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="bc1q7qt94pvsqdmahh6kfs2s0c496h7aj6k9yvfjfp"
          />
          <HelperText>Enter a Bitcoin address to validate</HelperText>
        </InputGroup>
        
        <InputGroup>
          <TerminalLabel htmlFor="network">Network</TerminalLabel>
          <TerminalSelect
            id="network"
            value={network}
            onChange={(e) => setNetwork(e.target.value)}
          >
            <option value="mainnet">Mainnet</option>
            <option value="testnet">Testnet</option>
            <option value="both">Both Networks</option>
          </TerminalSelect>
        </InputGroup>
        
        <TerminalButton 
          onClick={handleValidate}
          disabled={loading || !address}
          style={{ width: '100%', marginTop: '1rem' }}
        >
          {loading ? 'Validating...' : 'Validate Address'}
        </TerminalButton>
      </TerminalCard>

      {error && (
        <TerminalAlert className="error" style={{ marginTop: '1rem' }}>
          {error}
        </TerminalAlert>
      )}

      {result && (
        <ResultCard>
          <ResultHeader>
            <ValidationStatus>
              <StatusText isValid={result.isValid}>
                {result.isValid ? '[ VALID ]' : '[ INVALID ]'}
              </StatusText>
            </ValidationStatus>
            <NetworkChip>
              {getNetworkLabel(result.network || network)}
            </NetworkChip>
          </ResultHeader>

          <ResultSection>
            <SectionTitle>Address Details</SectionTitle>
            <DetailItem>
              <DetailLabel>Address:</DetailLabel>
              <AddressDisplay>
                {result.address || result.metadata?.address || address}
              </AddressDisplay>
            </DetailItem>
            
            {result.type_code && (
              <DetailItem>
                <DetailLabel>Type Code:</DetailLabel>
                <Chip className="info">{result.type_code}</Chip>
              </DetailItem>
            )}
            
            {result.type_desc && (
              <DetailItem>
                <DetailLabel>Type Desc:</DetailLabel>
                <TerminalText style={{ fontStyle: 'italic' }}>{result.type_desc}</TerminalText>
              </DetailItem>
            )}
            
            {result.network && (
              <DetailItem>
                <DetailLabel>Network:</DetailLabel>
                <Chip className="primary">{result.network}</Chip>
              </DetailItem>
            )}
          </ResultSection>

          <ResultGrid>
            <ResultSection>
              <SectionTitle>Validation Results</SectionTitle>
              <DetailItem>
                <DetailLabel>Valid:</DetailLabel>
                <Chip className={result.isValid ? 'success' : 'error'}>
                  {result.isValid ? 'YES' : 'NO'}
                </Chip>
              </DetailItem>
              
              {result.confidence && (
                <DetailItem>
                  <DetailLabel>Confidence:</DetailLabel>
                  <Chip className={
                    result.confidence > 0.8 ? 'success' : 
                    result.confidence > 0.5 ? 'warning' : 'error'
                  }>
                    {Math.round(result.confidence * 100)}%
                  </Chip>
                </DetailItem>
              )}
              
              {result.checksum !== undefined && (
                <DetailItem>
                  <DetailLabel>Checksum:</DetailLabel>
                  <Chip className={result.checksum ? 'success' : 'error'}>
                    {result.checksum ? 'VALID' : 'INVALID'}
                  </Chip>
                </DetailItem>
              )}
            </ResultSection>
            
            <ResultSection>
              <SectionTitle>Security Info</SectionTitle>
              {result.type_code && result.type_code.includes('P2PKH') && (
                <DetailItem>
                  <DetailLabel>Legacy Format:</DetailLabel>
                  <Chip className="warning">YES</Chip>
                </DetailItem>
              )}
              
              {result.type_code && (result.type_code.includes('P2SH') || result.type_code.includes('P2WPKH') || result.type_code.includes('P2WSH')) && (
                <DetailItem>
                  <DetailLabel>SegWit:</DetailLabel>
                  <Chip className="success">YES</Chip>
                </DetailItem>
              )}
              
              {result.type_code && (result.type_code.includes('P2WPKH') || result.type_code.includes('P2WSH')) && (
                <DetailItem>
                  <DetailLabel>Native SegWit:</DetailLabel>
                  <Chip className="success">YES</Chip>
                </DetailItem>
              )}
            </ResultSection>
          </ResultGrid>

          {result.message && (
            <ResultSection>
              <SectionTitle>Additional Information</SectionTitle>
              <TerminalText style={{ fontStyle: 'italic', margin: 0 }}>
                {result.message}
              </TerminalText>
            </ResultSection>
          )}

          {result.metadata && (
            <Metadata>
              Service: {result.metadata.service}
            </Metadata>
          )}
        </ResultCard>
      )}
    </Container>
  );
};

export default AddressValidator;
